import { Component, OnInit, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { AccessUser } from '../../../../core/models/system-settings/access-users.model';
import { ApiSystemSettingsService } from '../../../../core/services/api/system-settings/api-system-settings.service';
import { MapperAccessUsersService } from '../../../../core/mappers/system-settings/mapper-access-users.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { ModalManageUserComponent } from '../../modals/system-settings/modal-manage-user/modal-manage-user.component';
import { ModalUserLoginLogsComponent } from '../../modals/system-settings/modal-user-login-logs/modal-user-login-logs.component';
import { ModalUserSessionsComponent } from '../../modals/system-settings/modal-user-sessions/modal-user-sessions.component';

@Component({
  selector: 'app-manage-users',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PssLoadingComponent,
    ModalManageUserComponent,
    ModalUserLoginLogsComponent,
    ModalUserSessionsComponent,
  ],
  templateUrl: './manage-users.component.html',
  styleUrl: './manage-users.component.css'
})
export class ManageUsersComponent implements OnInit {
  private apiService = inject(ApiSystemSettingsService);
  private mapper = inject(MapperAccessUsersService);
  private toast = inject(ToastService);
  private router = inject(Router);
  private permissionService = inject(MenuPermissionService);

  users = signal<AccessUser[]>([]);
  isLoading = signal<boolean>(false);
  isDashboardOpen = signal<boolean>(true);

  searchField = signal<string>('display_name');
  searchText = signal<string>('');

  currentPage = signal<number>(1);
  pageSize = signal<number>(20);
  pageSizeOptions = [20, 50, 100];
  totalItems = signal<number>(0);
  totalPages = signal<number>(0);

  isModalOpen = signal<boolean>(false);
  selectedUser = signal<AccessUser | null>(null);
  isLogsOpen = signal<boolean>(false);
  logsUser = signal<AccessUser | null>(null);
  isSessionsOpen = signal<boolean>(false);
  sessionsUser = signal<AccessUser | null>(null);
  dashboardStats = signal<any[]>([]);

  searchFieldOptions = [
    { value: 'display_name', label: 'ชื่อที่แสดง' },
    { value: 'login', label: 'Login' },
    { value: 'cp_role', label: 'บทบาท (Role)' },
  ];

  Math = Math;

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  ngOnInit() {
    this.loadUsers();
  }

  toggleDashboard(): void {
    this.isDashboardOpen.set(!this.isDashboardOpen());
  }

  onRefresh(): void {
    this.currentPage.set(1);
    this.loadUsers();
  }

  onSearch(): void {
    this.currentPage.set(1);
    this.loadUsers();
  }

  navigateToCreate(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้างผู้ใช้');
      return;
    }
    this.selectedUser.set(null);
    this.isModalOpen.set(true);
  }

  navigateToEdit(user: AccessUser): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    this.selectedUser.set(user);
    this.isModalOpen.set(true);
  }

  openLogs(user: AccessUser): void {
    this.logsUser.set(user);
    this.isLogsOpen.set(true);
  }

  closeLogs(): void {
    this.isLogsOpen.set(false);
    this.logsUser.set(null);
  }

  openSessions(user: AccessUser): void {
    this.sessionsUser.set(user);
    this.isSessionsOpen.set(true);
  }

  closeSessions(): void {
    this.isSessionsOpen.set(false);
    this.sessionsUser.set(null);
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.selectedUser.set(null);
  }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  get pagesArray(): number[] {
    const total = this.totalPages();
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage() - 2);
    let endPage = Math.min(total, startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadUsers();
    }
  }

  onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize.set(Number(element.value));
    this.currentPage.set(1);
    this.loadUsers();
  }

  trackById(index: number, item: AccessUser): number {
    return item.id;
  }

  cityLoginBadgeClass(user: AccessUser): string {
    return user.cityLoginEnabled
      ? 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300'
      : 'bg-slate-100 text-slate-500';
  }

  cityLoginBadgeLabel(user: AccessUser): string {
    return user.cityLoginEnabled ? 'City Login เปิด' : 'City Login ปิด';
  }

  passwordBadgeClass(user: AccessUser): string {
    if (!user.hasCityPassword) return 'bg-slate-100 text-slate-500';
    if (user.mustChangePassword) return 'bg-amber-50 text-amber-700';
    return 'bg-emerald-50 text-emerald-700';
  }

  passwordBadgeLabel(user: AccessUser): string {
    if (!user.hasCityPassword) return 'ให้ตั้งเอง';
    if (user.mustChangePassword) return 'ต้องเปลี่ยนรหัส';
    return 'ตั้งแล้ว';
  }

  private calculateDashboardStats(total: number, users: AccessUser[]): void {
    const activeCount = users.filter(u => u.active).length;
    this.dashboardStats.set([
      { title: 'ผู้ใช้ทั้งหมด (หน้านี้)', value: users.length.toLocaleString(), icon: 'group', color: 'text-amber-500' },
      { title: 'รายการทั้งหมด', value: total.toLocaleString(), icon: 'storage', color: 'text-sky-500' },
      { title: 'Active (หน้านี้)', value: activeCount.toLocaleString(), icon: 'check_circle', color: 'text-emerald-500' },
    ]);
  }

  private loadUsers(): void {
    this.isLoading.set(true);
    const field = this.searchText().trim() ? this.searchField() : undefined;
    const text = this.searchText().trim() || undefined;

    this.apiService.getAccessUsersForTable(
      this.currentPage(),
      this.pageSize(),
      field,
      text
    ).subscribe({
      next: (res) => {
        const list = (res.data || []).map(d => this.mapper.mapAccessUserFromDto(d));
        this.users.set(list);
        this.totalItems.set(res.total ?? 0);
        this.totalPages.set(res.totalPages ?? 0);
        this.calculateDashboardStats(res.total ?? 0, list);
        this.isLoading.set(false);
      },
      error: (err: any) => {
        console.error(err);
        this.toast.error('ไม่สามารถโหลดข้อมูลผู้ใช้ได้');
        this.isLoading.set(false);
      }
    });
  }
}
